import { Download, ArrowRight, Phone, Mail, MapPin, Clock, MessageCircle, CheckCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useRef, useState } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import { FaMobile } from 'react-icons/fa';

const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwqxt_AfK8hiOhirOmJOYLUl5CvRZKom-rU-xFb4Dk9Y6UmhPTTzqohAArIe7HmwxOe/exec';

const SuccessPopup = ({ onClose }: { onClose: () => void }) => (
  <AnimatePresence>
    <motion.div
      key="backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div
        key="modal"
        initial={{ opacity: 0, scale: 0.85, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.85, y: 30 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="relative bg-white rounded-2xl shadow-2xl p-8 sm:p-10 text-center modal-width"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute close-icon w-8 h-8 rounded-full bg-[#2b2b2a]/10 flex items-center justify-center hover:bg-[#2b2b2a]/20 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-[#2b2b2a]" />
        </button>
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
          className="w-20 h-20 rounded-full  flex items-center justify-center mx-auto mb-2"
        >
          <CheckCircle className="w-10 h-10 text-[#c3a565]" strokeWidth={1.5} />
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <h3
            className="text-[#2b2b2a] text-2xl sm:text-3xl mb-3"
            style={{ fontWeight: 700 }}
          >
            Message Sent!
          </h3>
          <p className="text-[#2b2b2a]/60 text-sm sm:text-base mb-8 leading-relaxed">
            Thank you for reaching out. Our team will review your enquiry and get back to you within one business day.
          </p>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={onClose}
            className="w-full px-6 py-3.5 bg-[#2b2b2a] text-white rounded-full inline-flex items-center justify-center gap-2 hover:bg-[#2b2b2a]/90 transition-all duration-300 shadow-md cursor-pointer"
            style={{ fontWeight: 700, fontSize: '0.95rem' }}
          >
            Done
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

const ErrorPopup = ({ onClose }: { onClose: () => void }) => (
  <AnimatePresence>
    <motion.div
      key="backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div
        key="modal"
        initial={{ opacity: 0, scale: 0.85, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.85, y: 30 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="relative bg-white rounded-2xl shadow-2xl p-8 sm:p-10 text-center modal-width"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute close-icon w-8 h-8 rounded-full bg-[#2b2b2a]/10 flex items-center justify-center hover:bg-[#2b2b2a]/20 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-[#2b2b2a]" />
        </button>

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
          className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6"
        >
          <X className="w-10 h-10 text-red-500" strokeWidth={1.5} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <h3 className="text-[#2b2b2a] text-2xl sm:text-3xl mb-3" style={{ fontWeight: 700 }}>
            Something went wrong
          </h3>
          <p className="text-[#2b2b2a]/60 text-sm sm:text-base mb-8 leading-relaxed">
            Sorry, we couldn't send your message. Please try again or contact us directly at{' '}
            <a href="mailto:info@tamimilawfirm.com" className="text-[#c3a565] hover:underline">
              info@tamimilawfirm.com
            </a>
          </p>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={onClose}
            className="w-full px-6 py-3.5 bg-[#2b2b2a] text-white rounded-full inline-flex items-center justify-center gap-2 hover:bg-[#2b2b2a]/90 transition-all duration-300 shadow-md cursor-pointer"
            style={{ fontWeight: 700, fontSize: '0.95rem' }}
          >
            Try Again
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

export const CTASection = () => {
  const { ref, isVisible } = useScrollAnimation(0.2);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get the reCAPTCHA token directly from the ref
    const captchaToken = recaptchaRef.current?.getValue();


    
    if (!captchaToken || captchaToken.trim() === '') {
      alert("Please complete the reCAPTCHA verification");
      return;
    }

    setStatus('loading');

    const storedUtm = localStorage.getItem("fat_utm");
const landingPage = localStorage.getItem("fat_landing_page"); // ✅ match your tracker key

let utmData: Record<string, string> = {};

if (storedUtm) {
  try {
    utmData = JSON.parse(storedUtm);
  } catch (e) {
    console.error("Invalid UTM data in localStorage");
  }
}

    try {
      const payload = {
        date: new Date().toISOString().split('T')[0],
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
         practiceArea: formData.service, 
        subject: formData.service,
        message: formData.message.trim(),
          // ✅ UTM data
  ...utmData,
  landing_page: landingPage || "",

        'g-recaptcha-response': captchaToken, // Send token to backend for verification
      };

      // Note: Using no-cors mode means we can't read the response, so we assume success if no network error
      await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      // Reset form and captcha on success
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      resetCaptcha();
      setStatus('success');
    } catch (error) {
      console.error('Form submission error:', error);
      // Reset captcha on error so user can try again
      resetCaptcha();
      setStatus('error');
    }
  };

  const closePopup = () => {
    setStatus('idle');
    // Optionally reset captcha when popup is closed to keep it fresh
    if (status === 'error') {
      resetCaptcha();
    }
  };

  return (
    <>
      {status === 'success' && <SuccessPopup onClose={closePopup} />}
      {status === 'error' && <ErrorPopup onClose={closePopup} />}

      <section id="contact" aria-label="Contact Us" className="relative bg-[#faf9f7] py-20 sm:py-24 lg:py-28 overflow-hidden w-full">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#c3a565]/30 to-transparent"></div>
        <div className="absolute top-20 right-10 w-32 h-32 rounded-full bg-[#c3a565]/5 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 rounded-full bg-[#c3a565]/5 blur-3xl"></div>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
          <div className="mb-10 sm:mb-12 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-[#2b2b2a] text-2xl sm:text-3xl lg:text-4xl mb-3"
              style={{ fontWeight: 600 }}
            >
              Schedule Your Strategic Consultation
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="text-[#2b2b2a]/60 max-w-2xl mx-auto text-sm sm:text-base"
              style={{ fontWeight: 300 }}
            >
              Whether you're planning market entry, structuring a transaction, or navigating regulatory challenges, our team is ready to provide expert guidance.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="space-y-6 order-2 lg:order-1"
            >
              <div className="bg-white border border-[#2b2b2a]/10 rounded-2xl p-6 lg:p-7 shadow-lg">
                <span className="text-[#2b2b2a] mb-5" style={{ fontWeight: 600, fontSize: '1.25rem' }}>
                  Contact Information
                </span>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#c3a565]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-[#c3a565]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#2b2b2a]/60 mb-0.5 uppercase tracking-wider" style={{ fontWeight: 600 }}>Office</div>
                      <div className="text-[#2b2b2a] text-sm">Al Ashriah Street, Al Shatea 10th Floor, Ash Shati Ash Sharqi, Dammam 32414</div>
                    </div>
                  </div>

                  <div className="h-px bg-[#2b2b2a]/10"></div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#c3a565]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-[#c3a565]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#2b2b2a]/60 mb-0.5 uppercase tracking-wider" style={{ fontWeight: 600 }}>Email</div>
                      <a href="mailto:info@tamimilawfirm.com" className="text-[#2b2b2a] hover:text-[#c3a565] transition-colors text-sm">
                        info@tamimilawfirm.com
                      </a>
                    </div>
                  </div>

                  <div className="h-px bg-[#2b2b2a]/10"></div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#c3a565]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-[#c3a565]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#2b2b2a]/60 mb-0.5 uppercase tracking-wider" style={{ fontWeight: 600 }}>Phone</div>
                      <a href="tel:+966138223832" className="text-[#2b2b2a] hover:text-[#c3a565] transition-colors text-sm">
                        +966 13 822 3832
                      </a>
                    </div>
                  </div>

                  <div className="h-px bg-[#2b2b2a]/10"></div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#c3a565]/10 flex items-center justify-center flex-shrink-0">
                      <FaMobile className="w-4 h-4 text-[#c3a565]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#2b2b2a]/60 mb-0.5 uppercase tracking-wider" style={{ fontWeight: 600 }}>Mobile</div>
                     <div className='flex flex-col'>
                     <p>
  <a
    href="tel:+966561218881"
    className="block py-2 text-sm text-[#2b2b2a] hover:text-[#c3a565] transition-colors"
  >
    +966 56 121 8881
  </a>
</p>

<p>
  <a
    href="tel:+966567049111"
    className="block py-2 text-sm text-[#2b2b2a] hover:text-[#c3a565] transition-colors"
  >
    +966 56 704 9111
  </a>
</p>
                     </div>
                    </div>
                  </div>

                  <div className="h-px bg-[#2b2b2a]/10"></div>


                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#c3a565]/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-[#c3a565]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#2b2b2a]/60 mb-0.5 uppercase tracking-wider" style={{ fontWeight: 600 }}>Business Hours</div>
                      <div className="text-[#2b2b2a] text-sm">Sunday – Thursday: 8:00 AM – 6:00 PM</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative order-1 lg:order-2"
            >
              <div className="bg-white border border-[#2b2b2a]/10 rounded-2xl p-6 lg:p-8 shadow-lg">
                <h3 className="mb-5 text-[#2b2b2a] text-xl sm:text-2xl" style={{ fontWeight: 600 }}>
                  Get in Touch
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-[#2b2b2a]/70 mb-2 text-sm uppercase tracking-wider" style={{ fontWeight: 600 }}>
                      Full Name *
                    </label>
                    <input
                      type="text" id="name" name="name"
                      aria-required
                      value={formData.name} onChange={handleChange} required
                      className="w-full px-4 py-3.5 bg-[#faf9f7] border border-[#2b2b2a]/20 rounded-lg text-[#2b2b2a] placeholder-[#2b2b2a]/40 focus:outline-none focus:border-[#c3a565] focus:ring-2 focus:ring-[#c3a565]/30 transition-all text-base touch-manipulation"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-[#2b2b2a]/70 mb-2 text-sm uppercase tracking-wider" style={{ fontWeight: 600 }}>
                      Email Address *
                    </label>
                    <input
                      type="email" id="email" name="email"
                      value={formData.email} onChange={handleChange} required
                      className="w-full px-4 py-3.5 bg-[#faf9f7] border border-[#2b2b2a]/20 rounded-lg text-[#2b2b2a] placeholder-[#2b2b2a]/40 focus:outline-none focus:border-[#c3a565] focus:ring-2 focus:ring-[#c3a565]/30 transition-all text-base touch-manipulation"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-[#2b2b2a]/70 mb-2 text-sm uppercase tracking-wider" style={{ fontWeight: 600 }}>
                      Phone Number *
                    </label>
                    <input
                      type="tel" id="phone" name="phone"
                      value={formData.phone} onChange={handleChange} required
                      className="w-full px-4 py-3.5 bg-[#faf9f7] border border-[#2b2b2a]/20 rounded-lg text-[#2b2b2a] placeholder-[#2b2b2a]/40 focus:outline-none focus:border-[#c3a565] focus:ring-2 focus:ring-[#c3a565]/30 transition-all text-base touch-manipulation"
                      placeholder="+966 XX XXX XXXX"
                    />
                  </div>
                  <div>
                    <label htmlFor="service" className="block text-[#2b2b2a]/70 mb-2 text-sm uppercase tracking-wider" style={{ fontWeight: 600 }}>
                      Practice Area *
                    </label>
                    <select
                      id="service" name="service"
                      value={formData.service} onChange={handleChange} required
                      className="w-full px-4 py-3.5 bg-[#faf9f7] border border-[#2b2b2a]/20 rounded-lg text-[#2b2b2a] focus:outline-none focus:border-[#c3a565] focus:ring-2 focus:ring-[#c3a565]/30 transition-all text-base touch-manipulation"
                    >
                      <option value="">Select a practice area</option>
                      <option value="corporate commercial law">Corporate & Commercial Law</option>
                      <option value="compliance and complaince">Regulatory & Compliance</option>
                      <option value="investment and market entry">Investment & Market Entry</option>
                      <option value="contracts drafting and review">Contract Drafting & Review</option>
                      <option value="intellectual property">Intellectual Property</option>
                      <option value="dispute resolution">Dispute Resolution</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="flex justify-start">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6LfdgoQsAAAAACCHfOju-iXb9Nqj42KCJVCbtKtg"
                      onChange={() => {
                        // Optional: handle change if needed, token is retrieved on submit via ref
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-[#2b2b2a]/70 mb-2 text-sm uppercase tracking-wider" style={{ fontWeight: 600 }}>
                      Message *
                    </label>
                    <textarea
                      id="message" name="message"
                      value={formData.message} onChange={handleChange} required rows={4}
                      className="w-full px-4 py-3.5 bg-[#faf9f7] border border-[#2b2b2a]/20 rounded-lg text-[#2b2b2a] placeholder-[#2b2b2a]/40 focus:outline-none focus:border-[#c3a565] focus:ring-2 focus:ring-[#c3a565]/30 transition-all resize-none text-base touch-manipulation"
                      placeholder="Tell us about your legal needs..."
                    />
                  </div>
                  <motion.button
                    type="submit"
                    disabled={status === 'loading'}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full px-6 py-4 bg-[#2b2b2a] text-white rounded-full inline-flex items-center justify-center gap-3 hover:bg-[#2b2b2a]/90 transition-all duration-300 shadow-md touch-manipulation cursor-pointer ${
                      status === 'loading' ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                    style={{ fontWeight: 700, fontSize: '1rem' }}
                  >
                    {status === 'loading' ? (
                      <>
                        <svg className="animate-spin w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>
                </form>
              </div>
            </motion.div>
        
          </div>
        </div>
      </section>
    </>
  );
};